import React, { useEffect, useRef } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

/**
 * Ícone com micro-animação própria ao ser selecionado (design minimal v1).
 * Cada "motion" tem um movimento diferente, leve e curto, inspirado em ícones animados monocromáticos.
 * Puramente visual: não altera nenhuma lógica de quem o usa.
 */
export type IconMotion = 'flip' | 'spin' | 'pulse' | 'slide' | 'bounce' | 'wobble' | 'pop' | 'swing' | 'draw' | 'orbit';

const ease = [0.22, 1, 0.36, 1] as const;

const keyframes: Record<Exclude<IconMotion, 'draw'>, any> = {
  flip: { rotateY: [0, 180, 360], transition: { duration: 0.75, ease } },
  spin: { rotate: [0, -25, 380, 360], transition: { duration: 0.9, ease } },
  pulse: { scale: [1, 1.22, 0.94, 1.1, 1], transition: { duration: 0.7, ease } },
  slide: { x: [0, -4, 4, -2, 0], rotate: [0, -6, 5, 0, 0], transition: { duration: 0.6, ease } },
  bounce: { y: [0, -6, 0, -3, 0], transition: { duration: 0.65, ease } },
  wobble: { rotate: [0, -16, 12, -7, 3, 0], transition: { duration: 0.7, ease } },
  pop: { scale: [1, 0.78, 1.18, 1], transition: { duration: 0.5, ease } },
  swing: { rotate: [0, 18, -14, 8, -4, 0], transition: { duration: 0.8, ease } },
  orbit: { x: [0, 3, 0, -3, 0], y: [0, -3, -5, -3, 0], transition: { duration: 0.7, ease } },
};

interface AnimatedIconProps {
  /** O ícone já renderizado (ex.: <Brain />) */
  children: React.ReactNode;
  motion: IconMotion;
  /** Quando passa de false para true, a animação toca */
  active?: boolean;
  /** Toca também ao passar o mouse */
  playOnHover?: boolean;
  className?: string;
}

export const AnimatedIcon: React.FC<AnimatedIconProps> = ({ children, motion: kind, active = false, playOnHover = true, className = '' }) => {
  const controls = useAnimationControls();
  const ref = useRef<HTMLSpanElement>(null);

  const play = () => {
    if (kind === 'draw') {
      // Reinicia a animação CSS do traço NO MESMO elemento (sem remontar: não "rouba" o clique)
      const el = ref.current;
      if (!el) return;
      el.classList.remove('icon-draw');
      void el.offsetWidth; // força reflow para reiniciar a animação
      el.classList.add('icon-draw');
      return;
    }
    controls.start(keyframes[kind]);
  };

  useEffect(() => {
    if (active) play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <motion.span
      ref={ref}
      animate={controls}
      onHoverStart={playOnHover ? play : undefined}
      style={{ display: 'inline-flex', perspective: 400, transformOrigin: kind === 'swing' ? '50% 0%' : '50% 50%' }}
      className={className}
    >
      {children}
    </motion.span>
  );
};

export default AnimatedIcon;
